import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Modal, notification, Typography } from "antd";
import {
  BGMEA_UD_AMENDMENT_DELETE_ENDPOINT,
  BGMEA_UD_AMENDMENT_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData } from "apiServices/common";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BGMEA_UD_AMENDMENT_CREATE_PERMISSION,
  BGMEA_UD_AMENDMENT_DELETE_PERMISSION,
  BGMEA_UD_AMENDMENT_EDIT_PERMISSION,
  BGMEA_UD_AMENDMENT_VIEW_PERMISSION,
} from "routes/permissions";
import { hasPermission } from "utils/functions";

export default function BgmeaUdAmendment() {
  // Router
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: true,
        }),
      );
    } else {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: false,
        }),
      );
    }
  }, [showFilter, routerPath]);

  const getAllBgmeaUdCosts = useCallback(
    async (filterValues) => {
      const { ud_number } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      const query = `${BGMEA_UD_AMENDMENT_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(ud_number && { ud_number }),
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        const data = response.data?.data?.data || [];
        // Add unique key if id is missing
        const dataWithKeys = data.map((item, index) => ({
          ...item,
          key: item.id || `bgmea-ud-amendment-${index}`,
        }));

        setTableData(dataWithKeys);
        setMetaData({
          current: response.data?.data?.current_page || 1,
          defaultCurrent: response?.data?.data?.from || 1,
          defaultPageSize: response?.data?.data?.per_page || 10,
          total: response.data?.data?.total || 0,
        });
        setLoading(false);
      } else {
        setFetchError("Could not load this list.");
        setLoading(false);
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllBgmeaUdCosts(filterData);
  }, [refresh, filterData, getAllBgmeaUdCosts]);

  const refetch = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const deleteSelectedBgmeaUdCost = useCallback(
    async (id) => {
      Modal.confirm({
        title: "Confirm Deletion",
        content: "Are you sure you want to delete this BGMEA UD Amendment?",
        okText: "Yes, Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          const response = await deleteData(
            `${BGMEA_UD_AMENDMENT_DELETE_ENDPOINT}/${id}`,
          );

          if (
            response &&
            (response?.code === 200 || response?.status === 200)
          ) {
            setLoading(false);
            notification.success({
              message: "Success",
              description: "BGMEA UD Amendment deleted successfully",
              placement: "topRight",
            });
            refetch();
          }
        },
        onCancel: () => {
          setLoading(false);
        },
      });
    },
    [refetch],
  );

  const navigateToAdd = useCallback(() => {
    navigate("/import-export-expense-tracking/bgmea-ud-amendment/add");
  }, [navigate]);

  const navigateToEdit = useCallback(
    (record) => {
      navigate(
        `/import-export-expense-tracking/bgmea-ud-amendment/edit/${record.id}`,
      );
    },
    [navigate],
  );

  const navigateToView = useCallback(
    (record) => {
      navigate(
        `/import-export-expense-tracking/bgmea-ud-amendment/view/${record.id}`,
      );
    },
    [navigate],
  );

  const changePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "UD Number",
        dataIndex: ["ud_details", "ud_number"],
        width: "15%",
        key: "ud_number",
      },
      {
        title: "Sales Contract No",
        dataIndex: ["ud_details", "sale_contract_info", "reference_no"],
        width: "15%",
        key: "salesContractNumber",
      },
      {
        title: "Used Quantity",
        dataIndex: "used_quantity",
        width: "12%",
        key: "usedQuantity",
        ellipsis: true,
        render: (quantity) => quantity || "N/A",
      },
      {
        title: "Total Charge",
        dataIndex: "lien_charges",
        width: "15%",
        key: "totalAmount",
        ellipsis: true,
        render: (lienCharges) => {
          if (!lienCharges || !Array.isArray(lienCharges)) return "N/A";
          const total = lienCharges.reduce((sum, charge) => {
            return sum + (charge.exim_charge_amount || 0);
          }, 0);
          return total > 0 ? `${total} BDT` : "N/A";
        },
      },
      {
        title: "Action",
        key: "action",
        width: "10%",
        render: (_, record) => {
          const menuItems = [
            {
              key: "view",
              label: "View",
              icon: <EyeOutlined />,
              onClick: () => navigateToView(record),
              disabled: !hasPermission([BGMEA_UD_AMENDMENT_VIEW_PERMISSION]),
            },
            {
              key: "edit",
              label: "Edit",
              icon: <EditOutlined />,
              onClick: () => navigateToEdit(record),
              disabled: !hasPermission([BGMEA_UD_AMENDMENT_EDIT_PERMISSION]),
            },
            {
              type: "divider",
            },
            {
              key: "delete",
              label: "Delete",
              icon: <DeleteOutlined />,
              onClick: () => deleteSelectedBgmeaUdCost(record.id),
              disabled: !hasPermission([BGMEA_UD_AMENDMENT_DELETE_PERMISSION]),
              danger: true,
            },
          ];

          return (
            <Dropdown
              menu={{ items: menuItems }}
              placement="bottomRight"
              trigger={["hover"]}
            >
              <Button type="default" icon={<MoreOutlined />} />
            </Dropdown>
          );
        },
      },
    ],
    [deleteSelectedBgmeaUdCost, navigateToEdit, navigateToView],
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllBgmeaUdCosts}
        filterName="BGMEA_UD_Amendment"
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={navigateToAdd}
                disabled={
                  !hasPermission([BGMEA_UD_AMENDMENT_CREATE_PERMISSION])
                }
                addButtonText={
                  hasPermission([BGMEA_UD_AMENDMENT_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>BGMEA UD Amendment</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllBgmeaUdCosts(filterData)}
                dataSource={tableData}
                columns={tableColumns}
                rowKey="id"
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize || 10,
                  current: metaData?.current || 1,
                  onChange: changePage,
                  defaultCurrent: metaData?.defaultCurrent || 1,
                  total: metaData?.total || 0,
                  showTotal: (total) => `Total ${total} items`,
                }}
                bordered
                size="middle"
                scroll={{
                  x: "max-content",
                  y: tableData.length > 10 ? 500 : undefined,
                }}
                style={{ borderRadius: "8px", overflow: "hidden" }}
              />
            </div>
          </>
        }
      />
    </>
  );
}
